<template>
  <div class="ntn-certificate-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center flex-wrap">
        <h4 class="mb-0">NTN Certificate</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ certificates.length }} Staff
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading NTN certificates...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else-if="!certificates.length" class="text-center py-5">
      <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
      <p>No NTN certificate records found for the selected period</p>
    </div>

    <div v-else class="print-stack">
      <div
        v-for="(item, index) in certificates"
        :key="item.tID || item.employeeID || index"
        class="certificate-page"
      >
        <div class="cert-form">
          <h1 class="cert-form__title">
            <span class="cert-form__title-main">
              Certificate of Collection or Deduction of Income Tax (including
              salary)
            </span>
            <span class="cert-form__title-sub">(under rule 42)</span>
          </h1>

          <!-- Top meta row -->
          <div class="cert-form__meta-row">
            <span class="cert-form__meta-item">
              S.No.
              <span class="cert-form__meta-ul">{{ index + 1 }}</span>
            </span>
            <span class="cert-form__meta-item">
              Original/Duplicate
              <span class="cert-form__meta-ul">&nbsp;</span>
            </span>
            <span class="cert-form__meta-item">
              Date of issue.
              <span class="cert-form__meta-ul">{{ issueDateLabel }}</span>
            </span>
          </div>

          <!-- Main certificate body -->
          <div class="cert-form__body">
            <div class="cert-form__row">
              <span class="cert-form__label"
                >Certified that the sum of Rupees</span
              >
              <span class="cert-form__field cert-form__field--num cert-form__field--md">{{
                fieldValue(formatAmount(taxAmount(item)))
              }}</span>
              <span class="cert-form__hint"
                >(Amount of tax collected/deducted in figures)</span
              >
            </div>

            <div class="cert-form__row cert-form__row--wide">
              <span class="cert-form__field cert-form__field--grow cert-form__field--full-line">{{
                fieldValue(amountInWords(taxAmount(item)))
              }}</span>
              <span class="cert-form__hint">(amount in words)</span>
            </div>

            <p class="cert-form__text">on account of income tax has been</p>
            <p class="cert-form__text">
              deducted/collected from (Name and Address of the person from whom
              tax collected/deducted)
            </p>

            <span
              v-for="lineIndex in 3"
              :key="'emp-line-' + index + '-' + lineIndex"
              class="cert-form__field-full"
            >
              {{ fieldValue(employeeNameAddressLine(item, lineIndex - 1, true)) }}
            </span>
            <p class="cert-form__hint cert-form__hint--center">
              in case of an individual, his/her name in full and in case of an
              association of persons / company, name and style of the
              association of persons/company
            </p>

            <div class="cert-form__row">
              <span class="cert-form__label">having National Tax Number</span>
              <span class="cert-form__field cert-form__field--md">{{
                fieldValue(displayNtn(item))
              }}</span>
              <span class="cert-form__hint">(if any) and</span>
            </div>

            <div class="cert-form__row">
              <span class="cert-form__label">holder of CNIC No.</span>
              <span class="cert-form__field cert-form__field--md">{{
                fieldValue(displayCnic(item))
              }}</span>
              <span class="cert-form__hint">(In case of an individual)</span>
            </div>

            <div class="cert-form__row">
              <span class="cert-form__label">on</span>
              <span class="cert-form__field cert-form__field--sm">&nbsp;</span>
              <span class="cert-form__hint">(Date of collection/deduction)</span>
            </div>

            <div class="cert-form__row cert-form__row--period">
              <span class="cert-form__label">Or during the period</span>
              <span class="cert-form__period-fields">
                <span class="cert-form__period-label">From</span>
                <span class="cert-form__field cert-form__field--date">{{
                  periodFromShort
                }}</span>
                <span class="cert-form__period-label">To</span>
                <span class="cert-form__field cert-form__field--date">{{
                  periodToShort
                }}</span>
              </span>
              <span class="cert-form__hint">(Period of collection/deduction)</span>
            </div>

            <div class="cert-form__row">
              <span class="cert-form__label">under section</span>
              <span class="cert-form__field cert-form__field--md">{{
                fieldValue(display(item.section || item.taxSection) || "149/3")
              }}</span>
              <span class="cert-form__hint"
                >(specify the Section of Income Tax Ordinance, 2001)</span
              >
            </div>

            <div class="cert-form__row">
              <span class="cert-form__label">On account of</span>
              <span class="cert-form__field cert-form__field--md">{{
                fieldValue(display(item.accountOf || item.nature) || "SALARY")
              }}</span>
              <span class="cert-form__hint">(specify nature)</span>
            </div>

            <div class="cert-form__row">
              <span class="cert-form__label">vide</span>
              <span class="cert-form__field cert-form__field--md">{{
                fieldValue(display(item.vide || item.paymentMode) || "CHEQUE")
              }}</span>
              <span class="cert-form__hint"
                >(particulars of LC, Contract etc.)</span
              >
            </div>

            <div class="cert-form__row cert-form__row--salary">
              <span class="cert-form__label">on the value/amount of Rupee</span>
              <span class="cert-form__field cert-form__field--salary cert-form__field--num">{{
                fieldValue(formatAmount(taxableSalary(item)))
              }}</span>
              <span class="cert-form__salary-tag">Taxable Salary</span>
            </div>

            <span class="cert-form__field-full">{{
              fieldValue(amountInWords(taxableSalary(item)))
            }}</span>
          </div>

          <!-- Deposit details -->
          <p class="cert-form__deposit-intro">
            This is to further certify that the tax collected/deducted has been
            deposited in the Federal Government Account as per the following
            details:
          </p>

          <table class="cert-form__deposit">
            <thead>
              <tr>
                <th>Date of deposit.</th>
                <th>SBP/NBP/Treasury.</th>
                <th>Branch/City.</th>
                <th>Account</th>
                <th>(Rupees)</th>
                <th>Challan /Treasury No/CPR No.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in 7" :key="'dep-' + index + '-' + n">
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            </tbody>
          </table>

          <!-- Footer -->
          <p class="cert-form__footer-title">
            Company / office etc. collecting/deducting the tax:
          </p>

          <div class="cert-form__footer">
            <div class="cert-form__footer-info">
              <div class="cert-form__footer-row">
                <span class="cert-form__footer-label">Name.</span>
                <span class="cert-form__footer-ul">&nbsp;</span>
              </div>
              <div class="cert-form__footer-row cert-form__footer-row--address">
                <span class="cert-form__footer-label">Address.</span>
                <div class="cert-form__footer-ul-stack">
                  <span
                    v-for="lineIndex in 4"
                    :key="'addr-' + lineIndex"
                    class="cert-form__footer-ul"
                  >
                    &nbsp;
                  </span>
                </div>
              </div>
              <div class="cert-form__footer-row">
                <span class="cert-form__footer-label">NTN (if any)</span>
                <span class="cert-form__footer-ul">&nbsp;</span>
              </div>
              <div class="cert-form__footer-row">
                <span class="cert-form__footer-label">Date.</span>
                <span class="cert-form__footer-ul">&nbsp;</span>
              </div>
            </div>

            <div class="cert-form__footer-sign">
              <div class="cert-form__sign-row">
                <span class="cert-form__sign-tag">Signature</span>
                <div class="cert-form__sign-box"></div>
              </div>
              <div class="cert-form__sign-row">
                <span class="cert-form__sign-tag">Seal</span>
                <div class="cert-form__sign-box"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "NtnCertificatePrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      certificates: [],
      companyName: "",
      companyAddress: "",
      companyNtn: "",
      periodFrom: "",
      periodTo: "",
      db: "",
      cID: "",
      fromMonth: 7,
      fromYear: 2025,
      toMonth: 6,
      toYear: 2026,
    };
  },
  computed: {
    issueDateLabel() {
      const now = new Date();
      const day = now.getDate();
      const month = now.toLocaleDateString("en-GB", { month: "short" });
      const year = String(now.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    },
    periodFromShort() {
      return this.formatShortPeriodDate(this.fromMonth, this.fromYear, true);
    },
    periodToShort() {
      return this.formatShortPeriodDate(this.toMonth, this.toYear, false);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    display(value) {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    },
    fieldValue(value) {
      return this.display(value) || "\u00a0";
    },
    displayEmployeeName(item) {
      return this.display(
        item.name ||
          item.employeeName ||
          item.staffName ||
          item.fullName ||
          item.empName ||
          item.staff ||
          ""
      );
    },
    displayEmployeeAddress(item) {
      return this.display(
        item.address ||
          item.address_permanent ||
          item.addressPermanent ||
          item.employeeAddress ||
          item.staffAddress ||
          item.permanentAddress ||
          ""
      );
    },
    employeeNameAddressLine(item, lineIndex, raw) {
      const name = this.displayEmployeeName(item);
      const address = this.displayEmployeeAddress(item);

      let value = "";
      if (lineIndex === 0) {
        value = name;
      } else if (address) {
        const parts = address
          .split(/[\n,]+/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (parts.length) {
          if (parts.length === 1) {
            value = lineIndex === 1 ? parts[0] : "";
          } else if (lineIndex === 1) {
            value = parts.slice(0, -1).join(", ");
          } else if (lineIndex === 2) {
            value = parts[parts.length - 1];
          }
        }
      }

      if (raw) return value;
      return value || "\u00a0";
    },
    displayNtn(item) {
      return this.display(item.ntn || item.NTN || item.ntnNumber || "");
    },
    displayCnic(item) {
      return this.display(
        item.cnic ||
          item.CNIC ||
          item.cnicNo ||
          item.cnicNumber ||
          item.nic ||
          ""
      );
    },
    taxAmount(item) {
      return Number(item.incomeTaxDeduction || item.taxDeducted || 0) || 0;
    },
    taxableSalary(item) {
      const total = Number(item.total);
      if (Number.isFinite(total) && total > 0) return total;
      const basic = Number(item.basicPay) || 0;
      const allowances = Array.isArray(item.allowances)
        ? item.allowances.reduce(
            (sum, row) => sum + (Number(row && row.amount) || 0),
            0
          )
        : 0;
      return basic + allowances;
    },
    formatAmount(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n === 0) return "";
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatShortPeriodDate(month, year, isStart) {
      const m = Number(month) || 1;
      const y = Number(year) || new Date().getFullYear();
      const day = isStart ? 1 : new Date(y, m, 0).getDate();
      const monthLabel = new Date(y, m - 1, day).toLocaleDateString("en-GB", {
        month: "short",
      });
      const yearLabel = String(y).slice(-2);
      return `${day}-${monthLabel}-${yearLabel}`;
    },
    amountInWords(amount) {
      const n = Math.round(Number(amount) || 0);
      if (!n) return "";
      return `Rupees ${this.numberToWords(n)} Only`;
    },
    numberToWords(number) {
      const n = Math.floor(Math.abs(Number(number) || 0));
      if (n === 0) return "Zero";

      const ones = [
        "",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six",
        "Seven",
        "Eight",
        "Nine",
        "Ten",
        "Eleven",
        "Twelve",
        "Thirteen",
        "Fourteen",
        "Fifteen",
        "Sixteen",
        "Seventeen",
        "Eighteen",
        "Nineteen",
      ];
      const tens = [
        "",
        "",
        "Twenty",
        "Thirty",
        "Forty",
        "Fifty",
        "Sixty",
        "Seventy",
        "Eighty",
        "Ninety",
      ];

      const chunk = (num) => {
        if (num < 20) return ones[num];
        if (num < 100) {
          const t = Math.floor(num / 10);
          const o = num % 10;
          return tens[t] + (o ? ` ${ones[o]}` : "");
        }
        const h = Math.floor(num / 100);
        const rest = num % 100;
        return `${ones[h]} Hundred${rest ? ` ${chunk(rest)}` : ""}`.trim();
      };

      const crore = Math.floor(n / 10000000);
      const lakh = Math.floor((n % 10000000) / 100000);
      const thousand = Math.floor((n % 100000) / 1000);
      const hundred = n % 1000;
      const parts = [];

      if (crore) parts.push(`${chunk(crore)} Crore`);
      if (lakh) parts.push(`${chunk(lakh)} Lakh`);
      if (thousand) parts.push(`${chunk(thousand)} Thousand`);
      if (hundred) parts.push(chunk(hundred));

      return parts.join(" ").replace(/\s+/g, " ").trim();
    },
    flattenCertificates(payload) {
      const groups = Array.isArray(payload && payload.data)
        ? payload.data
        : [];
      const rows = [];

      groups.forEach((group) => {
        const staffList = Array.isArray(group.staff)
          ? group.staff
          : Array.isArray(group.certificates)
          ? group.certificates
          : [];

        if (
          !staffList.length &&
          (group.employeeID !== undefined || group.name !== undefined)
        ) {
          rows.push(group);
          return;
        }

        staffList.forEach((staff) => {
          rows.push({
            ...staff,
            campusID: staff.campusID || group.campusID,
            campusName: staff.campusName || group.campusName,
            companyName: staff.companyName || group.companyName,
            companyAddress: staff.companyAddress || group.companyAddress,
            companyNtn: staff.companyNtn || group.companyNtn,
          });
        });
      });

      return rows;
    },
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.fromMonth = Number(query.fromMonth) || 7;
      this.fromYear = Number(query.fromYear) || new Date().getFullYear();
      this.toMonth = Number(query.toMonth) || 6;
      this.toYear = Number(query.toYear) || new Date().getFullYear() + 1;

      const branch =
        this.$store.state.currentBranch ||
        (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
        {};
      this.companyName =
        branch.organizationName ||
        branch.name ||
        branch.branch ||
        this.$store.state.branchName ||
        "";
      this.companyAddress =
        branch.address ||
        branch.city ||
        branch.branchAddress ||
        "";
      this.companyNtn =
        branch.ntn || branch.NTN || branch.ntnNumber || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadReportData();
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${this.$store.state.domain}Salary/NtnCertificate`,
          {
            params: {
              db: this.db,
              cID: this.cID,
              fromMonth: this.fromMonth,
              fromYear: this.fromYear,
              toMonth: this.toMonth,
              toYear: this.toYear,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data;
        if (!payload || payload.status !== "success") {
          this.error =
            (payload && payload.message) ||
            "Unexpected response from NTN Certificate API.";
          this.certificates = [];
          return;
        }

        if (payload.periodFrom) this.periodFrom = payload.periodFrom;
        if (payload.periodTo) this.periodTo = payload.periodTo;
        if (payload.fromMonth) this.fromMonth = Number(payload.fromMonth);
        if (payload.fromYear) this.fromYear = Number(payload.fromYear);
        if (payload.toMonth) this.toMonth = Number(payload.toMonth);
        if (payload.toYear) this.toYear = Number(payload.toYear);

        if (payload.companyName) this.companyName = payload.companyName;
        if (payload.companyAddress) this.companyAddress = payload.companyAddress;
        if (payload.companyNtn) this.companyNtn = payload.companyNtn;

        this.certificates = this.flattenCertificates(payload);

        if (this.certificates.length) {
          const first = this.certificates[0];
          if (!this.companyName) {
            this.companyName = first.campusName || first.companyName || "";
          }
          if (!this.companyAddress) {
            this.companyAddress =
              first.companyAddress ||
              first.campusAddress ||
              first.employerAddress ||
              "";
          }
          if (!this.companyNtn) {
            this.companyNtn =
              first.companyNtn || first.campusNtn || first.employerNtn || "";
          }
        }
      } catch (err) {
        console.error("NTN Certificate load failed:", err);
        this.error =
          (err &&
            err.response &&
            err.response.data &&
            (err.response.data.message || err.response.data.data)) ||
          "Failed to load NTN certificates.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
$black: #000;
$line: #000;

.ntn-certificate-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: Arial, Helvetica, sans-serif;
  color: $black;
}

.control-bar {
  background: rgba(255, 255, 255, 0.97);
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  font-family: "Outfit", "Inter", sans-serif;

  h4 {
    font-weight: 700;
    margin: 0;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 700;
  border: none;
  cursor: pointer;
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.print-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
}

.certificate-page {
  background: #fff;
  width: 100%;
  max-width: 210mm;
  min-height: 297mm;
  padding: 10mm 14mm;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  page-break-after: always;
  display: flex;
  justify-content: center;
}

.cert-form {
  width: 100%;
  max-width: 182mm;
  margin: 0 auto;
  color: $black;
  background: #fff;
  font-size: 10.5px;
  line-height: 1.25;
}

.cert-form__title {
  text-align: center;
  font-size: 11px;
  margin: 0 0 8px;
  line-height: 1.25;
  color: $black;
}

.cert-form__title-main {
  display: block;
  font-weight: 700;
}

.cert-form__title-sub {
  display: block;
  font-weight: 700;
  margin-top: 1px;
}

.cert-form__meta-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 10px;
  flex-wrap: wrap;
}

.cert-form__meta-item {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
}

.cert-form__meta-ul {
  display: inline-block;
  min-width: 80px;
  border-bottom: 1px solid $black;
  padding: 0 4px 0;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.cert-form__body {
  margin-bottom: 6px;
}

.cert-form__text {
  margin: 0 0 3px;
  line-height: 1.25;
  color: $black;
}

.cert-form__row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 4px;
  min-height: 18px;
}

.cert-form__row--wide {
  margin-bottom: 5px;

  .cert-form__hint {
    flex: 0 0 auto;
    white-space: nowrap;
  }
}

.cert-form__row--period {
  flex-wrap: nowrap;

  .cert-form__hint {
    flex: 0 0 auto;
    max-width: 38%;
  }
}

.cert-form__row--salary {
  margin-top: 3px;
  margin-bottom: 5px;

  .cert-form__field--salary {
    flex: 1 1 auto;
    width: auto;
    min-width: 140px;
  }
}

.cert-form__label {
  flex: 0 0 auto;
  white-space: nowrap;
  line-height: 1.25;
}

.cert-form__field {
  display: block;
  flex: 0 0 auto;
  border-bottom: 1px solid $black;
  min-height: 16px;
  padding: 0 6px 1px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &--md {
    width: 160px;
    min-width: 160px;
  }

  &--num,
  &--salary {
    text-align: right;
  }

  &--grow,
  &--full-line {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
    white-space: normal;
  }

  &--sm {
    width: 90px;
    min-width: 90px;
  }

  &--date {
    width: 82px;
    min-width: 82px;
  }
}

.cert-form__field-full {
  display: block;
  width: 100%;
  border-bottom: 1px solid $black;
  min-height: 17px;
  margin-bottom: 3px;
  padding: 0 4px 1px;
  font-weight: 700;
  line-height: 1.2;
  box-sizing: border-box;
}

.cert-form__period-fields {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.cert-form__period-label {
  white-space: nowrap;
}

.cert-form__salary-tag {
  flex: 0 0 auto;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1.25;
}

.cert-form__hint {
  flex: 1 1 auto;
  font-size: 8.5px;
  line-height: 1.2;
  color: $black;
  min-width: 0;
  padding-bottom: 1px;

  &--center {
    flex: none;
    display: block;
    width: 100%;
    text-align: center;
    margin: 2px 0 4px;
    padding-bottom: 0;
  }
}

.cert-form__deposit-intro {
  margin: 6px 0 3px;
  line-height: 1.2;
  font-size: 10px;
  color: $black;
}

.cert-form__deposit {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 6px;

  th,
  td {
    border: 1px solid $line;
    padding: 2px 3px;
    text-align: center;
    vertical-align: middle;
    height: 16px;
    background: #fff;
    color: $black;
    font-size: 9px;
    font-weight: 400;
  }

  th {
    font-weight: 700;
    font-size: 8.5px;
    line-height: 1.1;
  }
}

.cert-form__footer-title {
  margin: 4px 0 3px;
  font-weight: 700;
  font-size: 10px;
  color: $black;
}

.cert-form__footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  margin-top: 6px;
  align-items: start;
}

.cert-form__footer-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cert-form__footer-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  line-height: 1.2;
}

.cert-form__footer-row--address {
  align-items: flex-start;
}

.cert-form__footer-label {
  flex: 0 0 72px;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1.2;
  font-size: 10.5px;
}

.cert-form__footer-ul {
  flex: 1 1 auto;
  display: block;
  min-width: 0;
  min-height: 16px;
  border-bottom: 1px solid $black;
  padding: 0 4px 1px;
  margin: 0;
  line-height: 1.2;
  font-size: 10.5px;
}

.cert-form__footer-ul-stack {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cert-form__footer-sign {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding-top: 18px;
}

.cert-form__sign-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.cert-form__sign-tag {
  flex: 0 0 62px;
  font-weight: 700;
  font-size: 10.5px;
  line-height: 1.2;
  padding-top: 3px;
}

.cert-form__sign-box {
  flex: 1 1 auto;
  border: 1px solid $line;
  background: #fff;
  height: 78px;
  min-width: 0;
  box-sizing: border-box;
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .no-print {
    display: none !important;
  }

  .ntn-certificate-report {
    background: #fff !important;
    padding: 0 !important;
    width: 100% !important;
    min-height: auto !important;
  }

  .print-stack {
    gap: 0 !important;
    align-items: stretch !important;
    width: 100% !important;
  }

  .certificate-page {
    box-shadow: none !important;
    max-width: 100% !important;
    width: 100% !important;
    min-height: 277mm !important;
    height: 277mm !important;
    padding: 0 !important;
    margin: 0 !important;
    page-break-after: always;
    break-after: page;
    display: flex !important;
    justify-content: center !important;
    box-sizing: border-box !important;
  }

  .certificate-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .cert-form {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 100% !important;
    font-size: 11.5px !important;
    line-height: 1.35 !important;
    background: #fff !important;
    color: #000 !important;
    font-family: Arial, Helvetica, sans-serif !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .cert-form__title {
    font-size: 13px !important;
    margin-bottom: 14px !important;
    line-height: 1.3 !important;
  }

  .cert-form__meta-row {
    margin-bottom: 18px !important;
    font-size: 11px !important;
  }

  .cert-form__meta-ul {
    min-width: 90px !important;
  }

  .cert-form__body {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-evenly !important;
    margin-bottom: 10px !important;
  }

  .cert-form__text {
    margin-bottom: 5px !important;
    line-height: 1.35 !important;
  }

  .cert-form__row {
    margin-bottom: 9px !important;
    min-height: 22px !important;
    gap: 10px !important;
  }

  .cert-form__row--wide {
    margin-bottom: 10px !important;
  }

  .cert-form__row--salary {
    margin-top: 6px !important;
    margin-bottom: 10px !important;
  }

  .cert-form__field {
    min-height: 20px !important;
    padding-bottom: 2px !important;
    font-size: 11.5px !important;

    &--md {
      width: 190px !important;
      min-width: 190px !important;
    }

    &--sm {
      width: 100px !important;
      min-width: 100px !important;
    }

    &--date {
      width: 92px !important;
      min-width: 92px !important;
    }

    &--salary {
      min-width: 180px !important;
    }
  }

  .cert-form__field-full {
    min-height: 22px !important;
    margin-bottom: 7px !important;
    font-size: 11.5px !important;
  }

  .cert-form__hint {
    font-size: 9px !important;

    &--center {
      margin: 4px 0 10px !important;
    }
  }

  .cert-form__deposit-intro {
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    font-size: 11px !important;
    line-height: 1.35 !important;
  }

  .cert-form__deposit {
    margin-bottom: 14px !important;

    th,
    td {
      height: 26px !important;
      font-size: 10px !important;
      padding: 4px !important;
      background: #fff !important;
      color: #000 !important;
    }

    th {
      font-size: 9.5px !important;
    }
  }

  .cert-form__footer-title {
    margin-bottom: 8px !important;
    font-size: 11px !important;
  }

  .cert-form__footer {
    gap: 24px !important;
    margin-top: 0 !important;
  }

  .cert-form__footer-info {
    gap: 7px !important;
  }

  .cert-form__footer-ul {
    min-height: 20px !important;
    font-size: 11px !important;
  }

  .cert-form__footer-ul-stack {
    gap: 7px !important;
  }

  .cert-form__footer-sign {
    gap: 14px !important;
    padding-top: 22px !important;
  }

  .cert-form__sign-box {
    height: 96px !important;
  }

  .cert-form__sign-tag,
  .cert-form__footer-label {
    font-size: 11px !important;
  }
}
</style>
